import {
  WSElement,
  WSFlexBox,
  WSIcon,
  WSPanel,
  WSPill,
  WSText
} from "@wingspanhq/fe-component-library";
import classNames from "classnames";
import React from "react";
import styles from "./index.module.scss";
import { StepPoint } from "./StepPoint";

export type NavigationStepItem = {
  key: string;
  title: string;
  /** Persistent amber indicator (unresolved warning on the step's content). */
  hasWarning?: boolean;
  /** Sub-items — rendered (as the Level2 dot list) while this step is active. */
  steps?: NavigationStepItem[];
};

type Props = {
  steps: NavigationStepItem[];
  /** Index of the active top-level step. */
  activeIndex: number;
  /** Index of the active sub-item within the active step (when it has steps). */
  activeSubIndex?: number;
  /** Completed entries become clickable to jump back when provided. */
  onStepClick?: (stepIndex: number, subStepIndex?: number) => void;
};

/**
 * The CONTROLLED variant of the Layout flow navigation rail: identical markup
 * and styles to `Navigation`, but driven by explicit active indices instead of
 * URL slugs — for wizards that swap visible content on one route (e.g. the
 * DynamicForm engine). Steps before the active one render as completed
 * (success pill / check point) and are clickable to jump back.
 */
export const NavigationSteps: React.FC<Props> = ({
  steps,
  activeIndex,
  activeSubIndex = 0,
  onStepClick
}) => (
  <WSElement className={styles.navigation}>
    {steps.map((step, index) => {
      const isActive = index === activeIndex;
      const isComplete = index < activeIndex;
      const isClickable = isComplete && !!onStepClick;

      return (
        <WSPanel
          key={step.key}
          className={classNames(
            styles.navigationItem,
            isActive && styles.navigationItemActive
          )}
          onClick={isClickable ? () => onStepClick?.(index) : undefined}
        >
          <WSFlexBox justify="space-between" wrap="nowrap">
            {/* Only the ACTIVE step reads bold; every inactive step (upcoming
                AND completed) stays gray/regular so the current position pops. */}
            <WSText
              weight={isActive ? "medium" : "book"}
              color={isActive ? "gray600" : "gray400"}
            >
              {step.title}
            </WSText>
            {step.hasWarning ? (
              <WSPill icon theme="warning" />
            ) : isComplete ? (
              <WSPill icon theme="success" />
            ) : null}
          </WSFlexBox>

          {isActive && step.steps && step.steps.length > 0 && (
            <WSElement className={styles.navigationLevel2}>
              {step.steps.map((subStep, subIndex) => {
                const isSubActive = subIndex === activeSubIndex;
                const isSubComplete = subIndex < activeSubIndex;
                const isSubClickable = isSubComplete && !!onStepClick;

                return (
                  <WSElement
                    key={subStep.key}
                    className={styles.navigationItem}
                    onClick={
                      isSubClickable
                        ? () => onStepClick?.(index, subIndex)
                        : undefined
                    }
                  >
                    {subStep.hasWarning ? (
                      // Same 24px footprint as StepPoint so the icon aligns
                      // with the dots and masks the Level2 connector line.
                      <WSElement className={styles.navigationSubItemWarning}>
                        <WSIcon name="alert-circle" size="S" color="amber400" />
                      </WSElement>
                    ) : (
                      <StepPoint
                        isActive={isSubActive}
                        isComplete={isSubComplete}
                      />
                    )}
                    <WSText.ParagraphXs
                      weight="medium"
                      color={
                        isSubActive || isSubComplete ? "gray600" : "gray300"
                      }
                    >
                      {subStep.title}
                    </WSText.ParagraphXs>
                  </WSElement>
                );
              })}
            </WSElement>
          )}
        </WSPanel>
      );
    })}
  </WSElement>
);
